<template>
  <div class="pop-bar-chart">
    <div
      class="chart-header"
    >
      <h2
        class="display-sm font-semibold"
        :class="{ 'not-available': !props.level }"
        data-test="level-name"
      >
        {{ props.level?.name || 'Not available' }}
      </h2>
      <span
        class="text-xs next-update-text"
        :class="{ 'not-available': !props.level }"
        data-test="next-update"
      >Next Update – {{ nextUpdateProgress }}/{{ nextUpdateTotal }} PQs</span>
    </div>
    <div
      class="chart"
      data-test="chart"
    >
      <div
        v-for="bar in chartLevels"
        :key="bar.cssKey"
        class="bar-column"
      >
        <div
          class="bar"
          :class="[`bar--${bar.cssKey}`, { 'bar--active': bar.isActive }]"
          :style="bar.isActive ? { backgroundColor: bar.color } : {}"
          :data-test="`bar-${bar.cssKey}`"
        >
          <template v-if="bar.isActive">
            <icon name="pop-bar-profile" />
          </template>
        </div>
        <span class="bar-label text-xs" data-test="bar-label">{{ bar.name }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import Icon from '@components/singletons/icon.vue';

interface LevelConfig {
  name: string;
  cssKey: string;
  color: string;
}

const LEVELS: LevelConfig[] = [
  { name: 'Low', cssKey: 'low', color: 'var(--utility-error-500)' },
  { name: 'Borderline', cssKey: 'borderline', color: 'var(--utility-warning-500)' },
  { name: 'High', cssKey: 'high', color: 'var(--utility-success-500)' },
  { name: 'Very High', cssKey: 'very_high', color: 'var(--utility-success-700)' },
];

const props = withDefaults(defineProps<{
  level?: { name: string } | null;
  nextUpdateProgress?: number;
  nextUpdateTotal?: number;
}>(), {
  level: null,
  nextUpdateProgress: 0,
  nextUpdateTotal: 0,
});

const chartLevels = computed(() =>
  LEVELS.map((config) => ({
    ...config,
    isActive: config.name === props.level?.name,
  })),
);
</script>

<style lang="scss" scoped>
.pop-bar-chart {
  width: 100%;
}

.chart-header {
  padding: 0 0 16px;

  h2 {
    margin: 0;
    color: var(--text-primary);

    &.not-available {
      color: transparent;
    }
  }
}

.next-update-text {
  color: var(--text-tertiary);
}

.not-available {
  color: transparent;
}

.chart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 0 var(--spacing-lg);
}

.bar-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 8px;
}

.bar {
  width: 100%;
  border-radius: var(--radius-xs) var(--radius-xs) 0 0;
  background-color: var(--Colors-Background-bg-disabled);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  overflow: hidden;

  &--low {
    height: 56px;
  }

  &--borderline {
    height: 82px;
  }

  &--high {
    height: 108px;
  }

  &--very_high {
    height: 136px;
  }
}

.bar-label {
  color: var(--text-tertiary);
  white-space: nowrap;
}
</style>
